import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useFeedbackModal, type FeedbackFormData, type FeedbackReason } from "./useFeedbackModal";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FeedbackFormData) => void;
}

const ReasonSection = styled("div")({});

const OtherInput = styled("input")(({ theme }) => ({
  display: "block",
  marginLeft: theme.spacing(4),
  marginBottom: theme.spacing(0.5),
  width: "calc(100% - 32px)",
  border: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  outline: "none",
  fontSize: "0.875rem",
  padding: "4px 0",
  backgroundColor: "transparent",
  color: theme.palette.text.primary,
}));

const ReasonItem = styled("div")({});

const DetailsSection = styled("div")({});

const DetailsTextarea = styled("textarea")(({ theme }) => ({
  width: "100%",
  resize: "vertical",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.shape.borderRadius}px`,
  padding: theme.spacing(1),
  fontSize: "0.875rem",
  fontFamily: "inherit",
  backgroundColor: "transparent",
  color: theme.palette.text.primary,
  outline: "none",
  boxSizing: "border-box",
  "&:focus": { borderColor: theme.palette.primary.main },
}));

const ModalTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
}));

const ModalContent = styled(DialogContent)({
  display: "flex",
  flexDirection: "column",
  gap: 24,
});

const ModalActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  gap: theme.spacing(1),
}));

const FullWidthFormControl = styled(FormControl)({
  width: "100%",
});

const SectionLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export function FeedbackModal({ open, onClose, onSubmit }: FeedbackModalProps) {
  const { t } = useTranslation();
  const { reason, setReason, otherText, setOtherText, details, setDetails, formData, reset } =
    useFeedbackModal();

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit() {
    onSubmit(formData);
    reset();
  }

  const REASONS: { value: FeedbackReason; label: string }[] = [
    { value: "unclear", label: t("feedback.reasonUnclear") },
    { value: "not_credible", label: t("feedback.reasonNotCredible") },
    { value: "biased", label: t("feedback.reasonBiased") },
    { value: "other", label: t("feedback.reasonOther") },
  ];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <ModalTitle>{t("feedback.modalTitle")}</ModalTitle>
      <ModalContent>
        <ReasonSection>
          <SectionLabel variant="body2" fontWeight={500}>
            {t("feedback.whatWentWrong")}
          </SectionLabel>
          <FullWidthFormControl component="fieldset">
            <RadioGroup value={reason} onChange={(_, val) => setReason(val as FeedbackReason)}>
              {REASONS.map(({ value, label }) => (
                <ReasonItem key={value}>
                  <FormControlLabel value={value} control={<Radio size="small" />} label={label} />
                  {value === "other" && reason === "other" && (
                    <OtherInput
                      type="text"
                      value={otherText}
                      onChange={(e) => setOtherText(e.currentTarget.value)}
                      placeholder={t("feedback.otherPlaceholder")}
                    />
                  )}
                </ReasonItem>
              ))}
            </RadioGroup>
          </FullWidthFormControl>
        </ReasonSection>

        <DetailsSection>
          <SectionLabel variant="body2" fontWeight={500}>
            {t("feedback.additionalDetails")}
          </SectionLabel>
          <DetailsTextarea
            value={details}
            onChange={(e) => setDetails(e.currentTarget.value)}
            rows={3}
          />
        </DetailsSection>
      </ModalContent>

      <ModalActions>
        <Button onClick={handleClose} color="inherit">
          {t("feedback.skip")}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disableElevation>
          {t("feedback.submit")}
        </Button>
      </ModalActions>
    </Dialog>
  );
}
